import React, { forwardRef, useContext, useMemo } from 'react';
import { parseColor } from '../utils.js';
import { MaterialContext } from './component-types.js';
const tempColor = new Float32Array(4);
/**
 * A React component that renders a rounded rectangle panel with customizable appearance.
 * Supports background color, border styling, and rounded corners.
 *
 * @component
 * @example
 * ```tsx
 * <Panel
 *   backgroundColor="#f0f0f0"
 *   borderColor="#333333"
 *   borderSize={2}
 *   rounding={10}
 *   padding={20}
 * >
 *   <Text>Panel Content</Text>
 * </Panel>
 * ```
 *
 * @param {PanelProps} props - The panel properties
 * @param {Material | null} [props.material] - Custom material for the panel. If not provided, uses cloned panel material from context
 * @param {Material | null} [props.borderMaterial] - Custom material for the border. If not provided, uses cloned panel material from context
 * @param {number} [props.rounding] - Corner rounding radius
 * @param {number} [props.resolution] - Resolution of the rounded corners
 * @param {Color} [props.backgroundColor='fff'] - Background color of the panel
 * @param {Color} [props.borderColor='fff'] - Border color of the panel
 * @param {number} [props.borderSize] - Width of the border
 * @param {React.Ref<Object3D>} ref - Forward ref to access the underlying 3D object
 * @returns {React.ReactElement} A rounded rectangle panel element
 */
export const Panel = forwardRef((props, ref) => {
    var _a, _b, _c, _d;
    const context = useContext(MaterialContext);
    const mat = useMemo(() => { var _a; return (_a = context.panelMaterial) === null || _a === void 0 ? void 0 : _a.clone(); }, []);
    mat &&
        mat.setColor(parseColor((_a = props.backgroundColor) !== null && _a !== void 0 ? _a : 'fff', tempColor));
    const bmat = useMemo(() => { var _a; return (_a = context.panelMaterial) === null || _a === void 0 ? void 0 : _a.clone(); }, []);
    bmat &&
        bmat.setColor(parseColor((_b = props.borderColor) !== null && _b !== void 0 ? _b : 'fff', tempColor));
    return React.createElement('roundedRectangle', Object.assign(Object.assign({}, props), { material: (_c = props.material) !== null && _c !== void 0 ? _c : mat, borderMaterial: (_d = props.borderMaterial) !== null && _d !== void 0 ? _d : bmat, ref: ref }), props.children);
});
Panel.displayName = 'Panel';
